/**
 * The player character: either a loaded GLB (with Walk/Idle animation groups)
 * or, by default, a procedural placeholder built from primitives so Phase 1 is
 * fully playable without any external asset.
 *
 * Node hierarchy:
 *   root      — ground position (x,z) + facing (rotation.y); follows the sim.
 *    └ visual — animation-only transform (bob/sway), never read by the camera.
 *
 * The sim is 2D ({x,y}); world position is (sim.x, height, sim.y). The camera
 * and input read `root.position`, which stays at ground level (the bob lives on
 * `visual`) so the camera never bounces.
 */
import { TransformNode } from "@babylonjs/core/Meshes/transformNode.js";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder.js";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial.js";
import { Color3 } from "@babylonjs/core/Maths/math.color.js";
import type { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import type { Scene } from "@babylonjs/core/scene.js";
import type { AnimationGroup } from "@babylonjs/core/Animations/animationGroup.js";
import { CHARACTER_GLB_URL } from "./characterConfig.js";
import { createHealthBar } from "./healthBar.js";

export interface Character {
  readonly root: TransformNode;
  /** Live reference to the ground-level world position (for camera + input). */
  readonly worldPos: Vector3;
  setGroundPosition(x: number, z: number): void;
  setFacing(angleRad: number): void;
  /** Show/hide the aim line (points along facing, i.e. the shot direction). */
  setAiming(on: boolean): void;
  /** Update the floating health bar (0..1). */
  setHealth(frac: number): void;
  /** Advance animation given current speed (units/sec) and frame dt (seconds). */
  update(speed: number, dt: number): void;
}

type AnimUpdate = (speed: number, dt: number) => void;

/** Capsule body + a forward-pointing wedge that shows facing. */
const buildPlaceholder = (scene: Scene, parent: TransformNode): AnimUpdate => {
  const body = MeshBuilder.CreateCapsule(
    "body",
    { height: 1.5, radius: 0.4, tessellation: 12, subdivisions: 1 },
    scene,
  );
  body.position.y = 0.75;
  body.parent = parent;
  const bodyMat = new StandardMaterial("bodyMat", scene);
  bodyMat.diffuseColor = new Color3(0.3, 0.65, 1);
  bodyMat.specularColor = new Color3(0.12, 0.12, 0.12);
  body.material = bodyMat;

  const wedge = MeshBuilder.CreateCylinder(
    "facing",
    { diameterTop: 0, diameterBottom: 0.34, height: 0.5, tessellation: 12 },
    scene,
  );
  wedge.rotation.x = Math.PI / 2; // point the cone along +Z (the facing axis)
  wedge.position.set(0, 0.9, 0.55);
  wedge.parent = parent;
  const wedgeMat = new StandardMaterial("wedgeMat", scene);
  wedgeMat.diffuseColor = new Color3(1, 0.85, 0.3);
  wedgeMat.emissiveColor = new Color3(0.3, 0.22, 0.05);
  wedge.material = wedgeMat;

  let clock = 0;
  return (speed: number, dt: number): void => {
    const moving = speed > 0.2 ? 1 : 0;
    clock += dt * (6 + speed); // bob faster the quicker we move
    parent.position.y = Math.sin(clock) * 0.06 * moving;
    parent.rotation.z = Math.sin(clock) * 0.05 * moving;
  };
};

/** Load a rigged GLB and wire its Walk/Idle animation groups. */
const loadGlb = async (
  scene: Scene,
  parent: TransformNode,
  url: string,
): Promise<AnimUpdate> => {
  // Lazy so the loader code stays out of the bundle until a GLB is configured.
  await import("@babylonjs/loaders/glTF/2.0/glTFLoader.js");
  const { SceneLoader } = await import("@babylonjs/core/Loading/sceneLoader.js");
  const result = await SceneLoader.ImportMeshAsync(null, "", url, scene);
  for (const mesh of result.meshes) {
    if (!mesh.parent) mesh.parent = parent;
  }

  const groups = result.animationGroups;
  groups.forEach((g) => g.stop());
  const walk = groups.find((g) => /walk|run/i.test(g.name)) ?? null;
  const idle = groups.find((g) => /idle/i.test(g.name)) ?? null;

  let current: AnimationGroup | null = null;
  const play = (g: AnimationGroup | null): void => {
    if (!g || current === g) return;
    current?.stop();
    g.start(true);
    current = g;
  };

  return (speed: number): void => {
    if (speed > 0.2) {
      play(walk ?? idle);
      if (walk) walk.speedRatio = Math.min(1.6, Math.max(0.6, speed / 5));
    } else {
      play(idle ?? walk);
    }
  };
};

export const createCharacter = async (scene: Scene): Promise<Character> => {
  const root = new TransformNode("character", scene);
  const visual = new TransformNode("character-visual", scene);
  visual.parent = root;

  let updateAnim: AnimUpdate;
  if (CHARACTER_GLB_URL) {
    try {
      updateAnim = await loadGlb(scene, visual, CHARACTER_GLB_URL);
    } catch (err) {
      // Never leave the player invisible — fall back to the placeholder.
      // eslint-disable-next-line no-console
      console.warn(`[AllStars] GLB load failed, using placeholder: ${String(err)}`);
      updateAnim = buildPlaceholder(scene, visual);
    }
  } else {
    updateAnim = buildPlaceholder(scene, visual);
  }

  // Aim line: a flat stripe along +Z (= facing = shot direction while aiming).
  const aimLine = MeshBuilder.CreateBox(
    "aim-line",
    { width: 0.16, height: 0.04, depth: 4.5 },
    scene,
  );
  aimLine.position.set(0, 0.05, 2.6);
  aimLine.parent = root;
  aimLine.isPickable = false;
  aimLine.setEnabled(false);
  const aimMat = new StandardMaterial("aimMat", scene);
  aimMat.disableLighting = true;
  aimMat.emissiveColor = new Color3(0.5, 0.85, 1);
  aimMat.alpha = 0.5;
  aimLine.material = aimMat;

  const bar = createHealthBar(scene, root);

  return {
    root,
    worldPos: root.position,
    setGroundPosition: (x: number, z: number): void => {
      root.position.x = x;
      root.position.z = z;
    },
    setFacing: (angleRad: number): void => {
      root.rotation.y = angleRad;
    },
    setAiming: (on: boolean): void => aimLine.setEnabled(on),
    setHealth: (frac: number): void => bar.set(frac),
    update: (speed: number, dt: number): void => updateAnim(speed, dt),
  };
};
